import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { AssessmentContext } from "../../contexts/assessments";
import { updateAssessment } from "../../axios/axiosFunctions";

const ASSESSMENT_OPTIONS = [
  { slug: "tim_self_assessment", label: "TIM — Team Assessment", is360: false },
  { slug: "executive_team_assessment", label: "Executive Team Assessment", is360: false },
  { slug: "mim_self_assessment", label: "MIM — Self-Assessment", is360: false },
  { slug: "lim_self_assessment", label: "LIM — Self-Assessment", is360: false },
  { slug: "mim_360_assessment", label: "MIM — 360 Assessment", is360: true },
  { slug: "lim_360_assessment", label: "LIM — 360 Assessment", is360: true },
];

const defaultRatingThresholds = {
  strength: { label: "STRENGTH", minScore: 90 },
  developing: { label: "DEVELOPING", minScore: 60 },
  opportunity: { label: "OPPORTUNITY" },
};

const defaultsBySlug = (slug, is360) => {
  const programName = slug.startsWith("mim")
    ? "Managers in Motion"
    : slug.startsWith("lim")
      ? "Leaders in Motion"
      : "Teams in Motion";
  const dimCount = slug === "lim_360_assessment" ? "six" : "four";
  return {
    coverSubtitle: is360 ? "360-Degree Assessment Feedback Report" : "Self-Assessment Report",
    summaryBannerTitle: is360
      ? `${programName}® 360-Degree Summary`
      : `${programName}® Summary`,
    summaryMethodology: is360
      ? `Your report reflects your confidence level on ${dimCount} dimensions and compares that to the perception of others. Each dimension had 10 related questions. To calculate your percentage score in each dimension, we counted the number of responses that were "Very Confident" or "Confident" and used these two answers to represent "Positive Responses". We then calculated the percent of your total answers that were "Positive Responses".`
      : `Your report reflects your confidence level on ${dimCount} dimensions. Each dimension had 10 related questions. To calculate your percentage score, we counted the number of responses that were "Very Confident" or "Confident" and used these to represent "Positive Responses".`,
    summaryCategorization:
      "For developmental purposes, we categorized your score into three different levels to help you focus your development priorities.",
    responseRateIntro: is360
      ? "You invited {invited} people to complete the survey. {responded} people responded."
      : "",
    summaryFollowingPages:
      "In the pages that follow, you can review your scores, question by question, to help you plan your development priorities.",
    blockSummaryIntro: `Here is a summary of your scores on each of the ${dimCount} blocks.`,
    ratingThresholds: defaultRatingThresholds,
  };
};

const ensureCopyDefaults = (assessment) => {
  const slug = assessment?.slug || "";
  const is360 = Boolean(assessment?.is360);
  const def = defaultsBySlug(slug, is360);
  const r = assessment?.report || {};
  return {
    welcome: r.welcome || "",
    howToUse: Array.isArray(r.howToUse) ? r.howToUse : [],
    coverSubtitle: r.coverSubtitle ?? def.coverSubtitle,
    summaryBannerTitle: r.summaryBannerTitle ?? def.summaryBannerTitle,
    summaryMethodology: r.summaryMethodology ?? def.summaryMethodology,
    summaryCategorization: r.summaryCategorization ?? def.summaryCategorization,
    responseRateIntro: r.responseRateIntro ?? def.responseRateIntro,
    summaryFollowingPages: r.summaryFollowingPages ?? def.summaryFollowingPages,
    blockSummaryIntro: r.blockSummaryIntro ?? def.blockSummaryIntro,
    ratingThresholds: {
      strength: {
        label: r.ratingThresholds?.strength?.label ?? def.ratingThresholds.strength.label,
        minScore: r.ratingThresholds?.strength?.minScore ?? def.ratingThresholds.strength.minScore,
      },
      developing: {
        label: r.ratingThresholds?.developing?.label ?? def.ratingThresholds.developing.label,
        minScore: r.ratingThresholds?.developing?.minScore ?? def.ratingThresholds.developing.minScore,
      },
      opportunity: {
        label: r.ratingThresholds?.opportunity?.label ?? def.ratingThresholds.opportunity.label,
      },
    },
  };
};

const AssessmentCopyAdmin = () => {
  const { isLoading, getAssessmentBySlug, refetchAssessments } = useContext(AssessmentContext);
  const [selectedSlug, setSelectedSlug] = useState(ASSESSMENT_OPTIONS[0].slug);
  const [form, setForm] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [is360, setIs360] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (isLoading) return;
    const a = getAssessmentBySlug(selectedSlug);
    if (!a) {
      setForm(null);
      setAssessmentId(null);
      return;
    }
    setAssessmentId(a._id);
    setIs360(Boolean(a.is360));
    setForm(ensureCopyDefaults(a));
  }, [isLoading, selectedSlug, getAssessmentBySlug]);

  const setField = (path, value) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cursor = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (cursor[keys[i]] == null) cursor[keys[i]] = {};
        cursor = cursor[keys[i]];
      }
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setHowToUseAt = (idx, value) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.howToUse[idx] = value;
      return next;
    });
  };
  const addHowToUse = () => {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.howToUse = [...(next.howToUse || []), ""];
      return next;
    });
  };
  const removeHowToUseAt = (idx) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.howToUse.splice(idx, 1);
      return next;
    });
  };

  const handleResetDefaults = () => {
    const a = getAssessmentBySlug(selectedSlug);
    if (!a) return;
    const def = defaultsBySlug(a.slug, Boolean(a.is360));
    setForm({
      welcome: form?.welcome || "",
      howToUse: form?.howToUse || [],
      ...def,
    });
    setAlert({ severity: "info", msg: "Reset hardcoded defaults locally — click Save to persist." });
  };

  const handleSave = async () => {
    if (!assessmentId || !form) return;
    setSaving(true);
    setAlert(null);
    const payload = {
      report: {
        welcome: form.welcome,
        howToUse: form.howToUse.filter((s) => s && s.trim().length > 0),
        coverSubtitle: form.coverSubtitle,
        summaryBannerTitle: form.summaryBannerTitle,
        summaryMethodology: form.summaryMethodology,
        summaryCategorization: form.summaryCategorization,
        responseRateIntro: form.responseRateIntro,
        summaryFollowingPages: form.summaryFollowingPages,
        blockSummaryIntro: form.blockSummaryIntro,
        ratingThresholds: form.ratingThresholds,
      },
    };
    try {
      const result = await updateAssessment(assessmentId, payload);
      if (result) {
        await refetchAssessments();
        setAlert({ severity: "success", msg: "Saved successfully" });
      } else {
        setAlert({ severity: "error", msg: "Save failed" });
      }
    } catch (e) {
      console.error(e);
      setAlert({ severity: "error", msg: "Save error" });
    } finally {
      setSaving(false);
      setTimeout(() => setAlert(null), 3500);
    }
  };

  if (isLoading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h3" fontWeight={500} color="secondary" sx={{ mb: 2 }}>
        Assessment Copy
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Edit the text shown in the generated PDF report for each assessment.
        Empty fields fall back to the system defaults.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 320 }}>
          <InputLabel>Assessment</InputLabel>
          <Select
            value={selectedSlug}
            label="Assessment"
            onChange={(e) => setSelectedSlug(e.target.value)}
          >
            {ASSESSMENT_OPTIONS.map((opt) => (
              <MenuItem key={opt.slug} value={opt.slug}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!form && (
        <Alert severity="warning">No assessment found for this slug.</Alert>
      )}

      {form && (
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }} elevation={1}>
            <Typography variant="h6" sx={{ mb: 2 }}>Cover Page</Typography>
            <TextField
              label="Cover subtitle"
              fullWidth
              size="small"
              value={form.coverSubtitle}
              onChange={(e) => setField("coverSubtitle", e.target.value)}
              helperText="e.g., '360-Degree Assessment Feedback Report' or 'Self-Assessment Report'"
            />
          </Paper>

          <Paper sx={{ p: 3 }} elevation={1}>
            <Typography variant="h6" sx={{ mb: 2 }}>Welcome / Intro Page</Typography>
            <TextField
              label="Welcome paragraph"
              fullWidth
              multiline
              minRows={4}
              value={form.welcome}
              onChange={(e) => setField("welcome", e.target.value)}
            />

            <Box sx={{ mt: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">"How to Use This Feedback" bullets</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addHowToUse}>
                  Add bullet
                </Button>
              </Box>
              <Stack spacing={1}>
                {(form.howToUse || []).map((bullet, idx) => (
                  <Box key={idx} display="flex" gap={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      multiline
                      size="small"
                      value={bullet}
                      onChange={(e) => setHowToUseAt(idx, e.target.value)}
                    />
                    <IconButton
                      onClick={() => removeHowToUseAt(idx)}
                      aria-label="remove bullet"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {(!form.howToUse || form.howToUse.length === 0) && (
                  <Typography variant="caption" color="textSecondary">
                    No bullets yet. Click "Add bullet" to start.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }} elevation={1}>
            <Typography variant="h6" sx={{ mb: 2 }}>Summary Page</Typography>
            <Stack spacing={2}>
              <TextField
                label="Summary banner title"
                fullWidth
                size="small"
                value={form.summaryBannerTitle}
                onChange={(e) => setField("summaryBannerTitle", e.target.value)}
                helperText='Banner heading on the Summary page (e.g., "MIM® 360-Degree Summary")'
              />
              <TextField
                label="Methodology paragraph"
                fullWidth
                multiline
                minRows={4}
                value={form.summaryMethodology}
                onChange={(e) => setField("summaryMethodology", e.target.value)}
                helperText="Paragraph describing how scores are calculated."
              />
              <TextField
                label="Categorization paragraph"
                fullWidth
                multiline
                minRows={3}
                value={form.summaryCategorization}
                onChange={(e) => setField("summaryCategorization", e.target.value)}
                helperText="Paragraph introducing the rating levels."
              />
              {is360 && (
                <TextField
                  label="Response rate intro (360 only)"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.responseRateIntro}
                  onChange={(e) => setField("responseRateIntro", e.target.value)}
                  helperText="Use {invited} and {responded} as placeholders."
                />
              )}
              <TextField
                label="'In the pages that follow…' sentence"
                fullWidth
                multiline
                minRows={2}
                value={form.summaryFollowingPages}
                onChange={(e) => setField("summaryFollowingPages", e.target.value)}
              />
              <TextField
                label="Block summary intro"
                fullWidth
                multiline
                minRows={2}
                value={form.blockSummaryIntro}
                onChange={(e) => setField("blockSummaryIntro", e.target.value)}
                helperText='Sentence shown above the "Block Summary Scores" chart.'
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }} elevation={1}>
            <Typography variant="h6" sx={{ mb: 2 }}>Rating Table</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Strength</Typography>
                  <TextField
                    label="Label"
                    size="small"
                    value={form.ratingThresholds.strength.label}
                    onChange={(e) => setField("ratingThresholds.strength.label", e.target.value)}
                  />
                  <TextField
                    label="Minimum % score"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    value={form.ratingThresholds.strength.minScore}
                    onChange={(e) =>
                      setField(
                        "ratingThresholds.strength.minScore",
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                  />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Developing</Typography>
                  <TextField
                    label="Label"
                    size="small"
                    value={form.ratingThresholds.developing.label}
                    onChange={(e) => setField("ratingThresholds.developing.label", e.target.value)}
                  />
                  <TextField
                    label="Minimum % score"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    value={form.ratingThresholds.developing.minScore}
                    onChange={(e) =>
                      setField(
                        "ratingThresholds.developing.minScore",
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                  />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Opportunity</Typography>
                  <TextField
                    label="Label"
                    size="small"
                    value={form.ratingThresholds.opportunity.label}
                    onChange={(e) => setField("ratingThresholds.opportunity.label", e.target.value)}
                  />
                  <Typography variant="caption" color="textSecondary">
                    Below the Developing threshold
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Divider />

          {alert && <Alert severity={alert.severity}>{alert.msg}</Alert>}

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={18} /> : null}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            <Button variant="outlined" onClick={handleResetDefaults}>
              Reset to defaults
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default AssessmentCopyAdmin;
